import React from 'react'
import Box from '@mui/system/Box'
import NextImage from 'next/image'
import { SxProps } from '@mui/material/styles'
import {
  CrossIcon,
  EmtChip,
  EmtDrawerLink,
  EmtIcon,
  EmtNav,
  EmtNavLink,
  EmtProfilBox,
  EmtSeparator,
  EmtTypography,
} from 'emt-components-v2'
import { useTranslation } from 'next-i18next'
import NavNextLink from '@/components/EMT/NavNextLink'
import NextALink from '../NextALink'
import { Routes } from '@/assets/configs'
import { useEMTState } from '@/store'
import DrawerNextLink from '@/components/EMT/DrawerNextLink'
import { useIsQueryOwner, useLogout } from '@/hooks'
import colors from '@/assets/themes/palettes/colors'
import { useUIStore } from '@/store/ui/uiStore'

import VariantImages from '@/assets/VariantImages'
import { useRouter } from 'next/router'
import NextMuiLink from '../NextMuiLink'
import CertifyButton from '@/components/EMT/CertifyButton'

import { usePetsitterUser } from '@/modules/PetsitterUser/hooks/usePetsitterUser'

type NavigationProps = {
  sx?: SxProps
}
enum ActiveItem {
  Dashboard = 'pro/',
  Reservations = 'pro/reservations',
  Missions = 'pro/missions',
  Services = 'pro/my-services',
}

export const NavigationPetSitter = ({ sx }: NavigationProps) => {
  const { t } = useTranslation('common')
  const { avatar, displayName } = usePetsitterUser()

  const { closeDrawer, isDrawerOpen, toggleDrawer, closeProfileMenu, openProfileMenu, isProfileMenuOpen } = useUIStore()
  const { onLogoutClickAsync } = useLogout()

  const {
    discussions: { unreadCount: discussionsUnreadCount },
  } = useEMTState()

  const [, , appendQueryType] = useIsQueryOwner(true)

  const messagesCount = discussionsUnreadCount

  const router = useRouter()

  let activeItem: ActiveItem | null = null
  switch (router.pathname) {
    case Routes.Pro.Dashboard:
      activeItem = ActiveItem.Dashboard
      break
    case Routes.Pro.Reservations:
      activeItem = ActiveItem.Reservations
      break
    case Routes.Pro.Missions:
      activeItem = ActiveItem.Missions
      break
    case Routes.Pro.Services:
      activeItem = ActiveItem.Services
      break
    default:
      break
  }

  const profilMenuChildren = [
    <Box
      onClick={() => {
        closeProfileMenu()
      }}
    >
      <NavNextLink key="edit-profile" href={appendQueryType(Routes.Pro.MyProfile)}>
        <EmtIcon className="emt-edit-1" sx={{ fontWeight: 'bold' }} />
        <EmtTypography variant="body2" fontWeight="bold">
          {t('navigation.editProfile')}
        </EmtTypography>
      </NavNextLink>
    </Box>,
    <EmtNavLink
      key="logout"
      onClick={() => {
        closeProfileMenu()
        onLogoutClickAsync()
      }}
    >
      <EmtIcon className="emt-logout" sx={{ fontWeight: 'bold' }} />
      <EmtTypography variant="body2" fontWeight="bold">
        {t('navigation.logout')}
      </EmtTypography>
    </EmtNavLink>,
  ]

  const drawerChildren = (
    <>
      <NextMuiLink href={appendQueryType(Routes.Pro.MyProfile)} sx={{ textDecoration: 'none' }} onClick={closeDrawer}>
        <EmtProfilBox username={displayName} secondaryLine={t('navigation.myProfile') as string} avatarUrl={avatar} />
      </NextMuiLink>
      <DrawerNextLink
        iconClassName="emt-briefcase-alt"
        title={t('navigation.drawer.services')}
        href={appendQueryType(Routes.Pro.Services)}
      />
      <DrawerNextLink
        iconClassName="emt-cog"
        title={t('navigation.drawer.settings')}
        href={appendQueryType(Routes.Pro.MyAccount.settings)}
      />
      <DrawerNextLink
        iconClassName="emt-envelope"
        title={t('navigation.drawer.contact')}
        href={appendQueryType(Routes.Pro.Contact)}
      />

      <DrawerNextLink
        icon={
          <Box sx={{ marginRight: '7px' }}>
            <CrossIcon color={colors.purple.main} />
          </Box>
        }
        title={t('navigation.drawer.assistance')}
        href={appendQueryType(Routes.Pro.Assistance)}
      />
      <DrawerNextLink
        iconClassName="emt-lock"
        title={t('navigation.drawer.safety')}
        href={appendQueryType(Routes.Pro.Safety)}
      />
      <DrawerNextLink
        iconClassName="emt-question-circle"
        title={t('navigation.drawer.help.title')}
        href={t('navigation.drawer.help.petsitting.url')}
      />

      <CertifyButton />

      <EmtSeparator />

      <Box
        onClick={() => {
          closeDrawer()
          onLogoutClickAsync()
        }}
      >
        <EmtDrawerLink sx={{ '& .emt-right-1': { display: 'none' } }}>
          <EmtIcon className="emt-logout" color="primary" />
          <EmtTypography variant="body1" color="black">
            {t('navigation.logout')}
          </EmtTypography>
        </EmtDrawerLink>
      </Box>
    </>
  )

  const mobileChildren = (
    <>
      <NavNextLink
        sx={{ '&:hover p, &:hover span, &.active span': { color: '#AE59C6!important' } }}
        active={activeItem === ActiveItem.Dashboard}
        href={appendQueryType(Routes.Pro.Dashboard)}
      >
        <EmtIcon className="emt-book-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.dashboard')}
        </EmtTypography>
      </NavNextLink>
      <NavNextLink
        sx={{ '&:hover p, &:hover span, &.active span': { color: '#AE59C6!important' } }}
        active={activeItem === ActiveItem.Reservations}
        notification={!!messagesCount}
        href={appendQueryType(Routes.Pro.Reservations)}
      >
        <EmtIcon className="emt-message-text-alt-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.reservations')}
        </EmtTypography>
      </NavNextLink>
      <NavNextLink
        sx={{ '&:hover p, &:hover span, &.active span': { color: '#AE59C6!important' } }}
        active={activeItem === ActiveItem.Missions}
        notification={!!messagesCount}
        href={appendQueryType(Routes.Pro.Missions)}
      >
        <EmtIcon className="emt-note-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.missions')}
        </EmtTypography>
      </NavNextLink>
    </>
  )

  return (
    <EmtNav
      drawerChildren={drawerChildren}
      drawerTitle={t('navigation.menu') as string}
      mobileChildren={mobileChildren}
      avatarUrl={avatar}
      profilTitle={t('navigation.myProfile') as string}
      profilMenuChildren={profilMenuChildren}
      isDrawerOpen={isDrawerOpen}
      toggleDrawer={toggleDrawer}
      closeDrawer={closeDrawer}
      isProfileMenuOpen={isProfileMenuOpen}
      closeProfileMenu={closeProfileMenu}
      openProfileMenu={openProfileMenu}
      sx={{
        ...sx,
        '& .MuiPaper-rounded.MuiPopover-paper': {
          zIndex: 9999999999,
        },
      }}
    >
      <NextALink href={appendQueryType(Routes.Pro.Dashboard)}>
        <NextImage src={VariantImages.LogoSitterBlackFull} width={167} quality={100} alt={'Site logo'} />
      </NextALink>
      <NavNextLink
        sx={{ '&:hover p, &:hover span, &.active p, &.active span': { color: '#AE59C6!important' } }}
        active={activeItem === ActiveItem.Dashboard}
        href={appendQueryType(Routes.Pro.Dashboard)}
      >
        <EmtIcon className="emt-book-1" sx={{ fontSize: '16px' }} />
        <EmtTypography variant="body2" fontWeight="bold">
          {t('navigation.dashboard')}
        </EmtTypography>
      </NavNextLink>
      <NavNextLink
        sx={{ '&:hover p, &:hover span, &.active p, &.active span': { color: '#AE59C6!important' } }}
        active={activeItem === ActiveItem.Reservations}
        href={appendQueryType(Routes.Pro.Reservations)}
      >
        <EmtIcon className="emt-message-text-alt-1" sx={{ fontSize: '16px' }} />
        <EmtTypography variant="body2" fontWeight="bold">
          {t('navigation.reservations')}
        </EmtTypography>
        {!!messagesCount && <EmtChip color="primary" type="roundedNumber" label={messagesCount} />}
      </NavNextLink>
      <NavNextLink
        sx={{ '&:hover p, &:hover span, &.active p, &.active span': { color: '#AE59C6!important' } }}
        active={activeItem === ActiveItem.Missions}
        href={appendQueryType(Routes.Pro.Missions)}
      >
        <EmtIcon className="emt-note-1" sx={{ fontSize: '16px' }} />
        <EmtTypography variant="body2" fontWeight="bold">
          {t('navigation.missions')}
        </EmtTypography>
        {!!messagesCount && <EmtChip color="primary" type="roundedNumber" label={messagesCount} />}
      </NavNextLink>
      <NavNextLink
        sx={{ '&:hover p, &:hover span, &.active p, &.active span': { color: '#AE59C6!important' } }}
        active={activeItem === ActiveItem.Services}
        href={appendQueryType(Routes.Pro.Services)}
      >
        <EmtIcon className="emt-briefcase-alt-1" sx={{ fontSize: '16px' }} />
        <EmtTypography variant="body2" fontWeight="bold">
          {t('navigation.services')}
        </EmtTypography>
      </NavNextLink>
    </EmtNav>
  )
}
